import { useState, useEffect } from 'react';
import { XCircle } from '@untitledui/icons';
import { ModalOverlay, Modal, Dialog } from '@/components/application/modals/modal';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { useTranslation } from 'react-i18next';
import { receiverTargetService } from '@/data/mock-receiver-targets';

interface TargetEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    receiverId: string;
    year: number;
    weekNumber: number;
    onSave: () => void;
}

export const TargetEditModal = ({
    isOpen,
    onClose,
    receiverId,
    year,
    weekNumber,
    onSave,
}: TargetEditModalProps) => {
    const { t } = useTranslation(['pages:calendar', 'common']);
    const [containerTarget, setContainerTarget] = useState<string>('');
    const [revenueTarget, setRevenueTarget] = useState<string>('');
    const [useDefault, setUseDefault] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const currentTarget = receiverTargetService.getTarget(receiverId, year, weekNumber);
            const defaultTarget = receiverTargetService.getDefaultTarget(receiverId, year);
            
            if (currentTarget && currentTarget.weekNumber !== undefined) {
                // C'è un override specifico
                setContainerTarget(currentTarget.containerTarget.toString());
                setRevenueTarget(currentTarget.revenueTarget.toString());
                setUseDefault(false);
            } else if (defaultTarget) {
                // Usa il target generico
                setContainerTarget(defaultTarget.containerTarget.toString());
                setRevenueTarget(defaultTarget.revenueTarget.toString());
                setUseDefault(true);
            } else {
                setContainerTarget('0');
                setRevenueTarget('0');
                setUseDefault(true);
            }
        }
    }, [isOpen, receiverId, year, weekNumber]);

    const handleSave = async () => {
        setIsLoading(true);
        try {
            const containerValue = parseFloat(containerTarget) || 0;
            const revenueValue = parseFloat(revenueTarget) || 0;

            if (useDefault) {
                // Resetta al target generico
                receiverTargetService.resetToDefault(receiverId, year, weekNumber);
            } else {
                // Salva override specifico
                receiverTargetService.setTarget(
                    receiverId,
                    year,
                    containerValue,
                    revenueValue,
                    weekNumber
                );
            }
            
            onSave();
            onClose();
        } catch (error) {
            console.error('Error saving target:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReset = () => {
        const defaultTarget = receiverTargetService.getDefaultTarget(receiverId, year);
        if (defaultTarget) {
            setContainerTarget(defaultTarget.containerTarget.toString());
            setRevenueTarget(defaultTarget.revenueTarget.toString());
            setUseDefault(true);
        }
    };

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={onClose}>
            <Modal>
                <Dialog>
                    <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold text-primary">
                                {t('targetModal.title', { week: weekNumber })}
                            </h2>
                            <button
                                onClick={onClose}
                                className="text-tertiary hover:text-primary transition-colors"
                            >
                                <XCircle className="size-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-primary mb-2">
                                    {t('targetModal.containerTarget')}
                                </label>
                                <Input
                                    type="number"
                                    value={containerTarget}
                                    onChange={(value) => setContainerTarget(value)}
                                    isDisabled={useDefault}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-primary mb-2">
                                    {t('targetModal.revenueTarget')}
                                </label>
                                <Input
                                    type="number"
                                    value={revenueTarget}
                                    onChange={(value) => setRevenueTarget(value)}
                                    isDisabled={useDefault}
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="useDefault"
                                    checked={useDefault}
                                    onChange={(e) => setUseDefault(e.target.checked)}
                                    className="rounded border-secondary_alt"
                                />
                                <label htmlFor="useDefault" className="text-sm text-tertiary">
                                    {t('targetModal.useDefault')}
                                </label>
                            </div>

                            {!useDefault && (
                                <Button
                                    color="secondary"
                                    size="sm"
                                    onClick={handleReset}
                                >
                                    {t('targetModal.resetToDefault')}
                                </Button>
                            )}
                        </div>

                        <div className="flex gap-3 mt-6">
                            <Button
                                color="secondary"
                                onClick={onClose}
                                disabled={isLoading}
                                className="flex-1"
                            >
                                {t('common:cancel', { ns: 'common' })}
                            </Button>
                            <Button
                                color="primary"
                                onClick={handleSave}
                                disabled={isLoading}
                                className="flex-1"
                            >
                                {isLoading ? t('common:loading', { ns: 'common' }) : t('common:save', { ns: 'common' })}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
};

